import { z } from 'zod';
import { DUTY_BASES, DUTY_STATES, GYM_MODULE_KEYS, NETWORK_STATUSES, PORTALS, ROLES, PERMISSIONS, SHIFT_ACCESS_MODES, THEME_PREFERENCES, VIEW_AS_ROLES } from '@bulk/types';
import { idSchema, localeSchema } from './common';

export const passwordSchema = z.string().min(8).max(128);

export const inviteTokenSchema = z.string().trim().min(1).max(200);

export const registerSchema = z.object({
  email: z.email().toLowerCase(),
  password: passwordSchema,
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  phone: z.string().trim().max(32).optional(),
  locale: localeSchema.optional(),
  inviteToken: inviteTokenSchema.optional(),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const registerMemberSchema = z.object({
  inviteToken: inviteTokenSchema.optional(),
});
export type RegisterMemberInput = z.infer<typeof registerMemberSchema>;

export const loginSchema = z.object({
  email: z.email().toLowerCase(),
  password: z.string().min(1).max(128),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const authSessionSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().nullable(),
  expiresAt: z.string().nullable(),
});
export type AuthSession = z.infer<typeof authSessionSchema>;

export const gymMembershipContextSchema = z.object({
  gymId: idSchema,
  gymName: z.string(),
  gymSlug: z.string(),
  role: z.enum(ROLES),
  memberId: idSchema.nullable(),
  locationIds: z.array(idSchema).default([]).describe('The branches this person works at; empty means every branch'),
  practiceOfGymId: idSchema.nullable().default(null).describe('Set on a practice gym: the real gym whose staff train in it'),
  ownRole: z.enum(ROLES).nullable().default(null).describe("A platform admin's own staff role at this gym, beside the gym admin standing Bulk gives them; send it as X-Gym-Role to work in it"),
  brand: z.object({ logoUrl: z.string().nullable(), primaryColor: z.string(), secondaryColor: z.string() }).nullable(),
});
export type GymMembershipContext = z.infer<typeof gymMembershipContextSchema>;

export const signInAddressSchema = z.object({
  gymId: idSchema.nullable().describe("Null for Bulk's own address"),
  gymName: z.string().nullable(),
  portal: z.enum(PORTALS),
  url: z.string().describe('The sign-in page at that address'),
});
export type SignInAddressDto = z.infer<typeof signInAddressSchema>;

export const viewAsMemberSchema = z.object({
  id: idSchema,
  memberNumber: z.string(),
  firstName: z.string(),
  lastName: z.string(),
});
export type ViewAsMemberDto = z.infer<typeof viewAsMemberSchema>;

export const viewAsMembersQuerySchema = z.object({
  q: z.string().trim().max(100).optional().describe('Part of a name or member number'),
});

export const dutySchema = z.object({
  bound: z.boolean().describe('Whether the gym holds this person to their shifts: its shift access is switched on and lists their role'),
  mode: z.enum(SHIFT_ACCESS_MODES),
  state: z.enum(DUTY_STATES).describe('ON while a rostered shift (with its grace minutes), a clock-in or an extension lasts; always ON when not bound'),
  basis: z.enum(DUTY_BASES).nullable().describe('What keeps them on duty now'),
  until: z.string().nullable().describe('When working permissions end; null while clocked in, which lasts until clocking out, and when off duty'),
  endedAt: z.string().nullable().describe('Off duty: when working permissions last ended'),
  nextStartsAt: z.string().nullable().describe('When the next rostered shift starts, within the coming week'),
  warnBeforeMinutes: z.number().int().describe('How long before `until` the app warns'),
});
export type DutyDto = z.infer<typeof dutySchema>;

export const currentUserSchema = z.object({
  id: idSchema,
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  phone: z.string().nullable(),
  locale: localeSchema,
  theme: z.enum(THEME_PREFERENCES),
  isPlatformAdmin: z.boolean(),
  gyms: z.array(gymMembershipContextSchema),
  currentGym: gymMembershipContextSchema.nullable(),
  suspendedGym: gymMembershipContextSchema.nullable(),
  permissions: z.array(z.enum(PERMISSIONS)).describe('What this request may do: from outside the gym\'s allowed networks, only reading and team self-service'),
  network: z.enum(NETWORK_STATUSES).default('UNRESTRICTED').describe("Where this request came from against the gym's allowed networks"),
  disabledModules: z.array(z.enum(GYM_MODULE_KEYS)).default([]).describe('The modules Bulk switched off for the gym this request works in, dependents included: nothing of them is shown'),
  signInAddresses: z.array(signInAddressSchema).default([]).describe('Every address this person signs in at'),
  signInElsewhere: z.boolean().default(false).describe('This address is not one of theirs while sign-in by address is on: nothing here is open to them'),
  viewAs: z
    .enum([...VIEW_AS_ROLES, 'MEMBER'])
    .nullable()
    .default(null)
    .describe("The role a gym admin, or Bulk acting in the gym, is working the admin as (X-View-As), or MEMBER while viewing the member area as one of a practice gym's made-up members (X-View-As-Member); currentGym.role, currentGym.memberId and permissions are then that role's or that member's"),
  viewAsRoles: z.array(z.enum(VIEW_AS_ROLES)).default([]).describe('The roles this person may switch the admin into at this gym: every staff role for a gym admin or Bulk, none for anyone else'),
  viewAsMember: viewAsMemberSchema.nullable().default(null).describe('The made-up member whose member area this is, while viewAs is MEMBER'),
  viewAsMembers: z.boolean().default(false).describe("Whether this person may view the member area as one of this gym's made-up members: in a practice gym only, for a gym admin or Bulk"),
  celebrate: z
    .object({ kind: z.literal('BIRTHDAY') })
    .nullable()
    .default(null)
    .describe("Something to celebrate with this person today in the gym's time zone: their birthday, from their employee or member record at the current gym, while the gym greets that group"),
  duty: dutySchema.nullable().default(null).describe('Where this person stands against their shifts at the current gym; null without a staff role there; never bound while viewing the admin as another role'),
});
export type CurrentUser = z.infer<typeof currentUserSchema>;

export const updateProfileSchema = z.object({
  firstName: z.string().trim().min(1).max(80).optional(),
  lastName: z.string().trim().min(1).max(80).optional(),
  phone: z.string().trim().max(32).nullable().optional(),
  locale: localeSchema.optional(),
  theme: z.enum(THEME_PREFERENCES).optional(),
  smsNotifications: z.boolean().optional(),
  smsOffers: z.boolean().optional(),
  emailOffers: z.boolean().optional(),
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;
